import * as Sentry from '@sentry/react';
import { motion } from 'framer-motion';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useMatch, useOutletContext } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ModalContainer } from '../components/__design-system/modal/modal-container';
import { AnimatedImageGallery } from '../components/animated-image-gallery/AnimatedImageGallery';
import { MatterportEnvironment } from '../components/matterport-environment/MatterportEnvironment';
import ScrollToHashElement from '../components/scroll-to-hash-element/ScrollToHashElement';
import { useMatterport } from '../context/new/MatterportContext';
import { useViewToggle } from '../context/ViewToggleProvider';
import { useScreenSize } from '../hooks/useScreenSize';
import { RestaurantGuardOutletContext } from '../routes/RestaurantGuard';
import { DesktopHeader } from './DesktopHeader';

import 'react-toastify/dist/ReactToastify.css';

export interface AppLayoutProps {
	children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
	const parentContext = useOutletContext<RestaurantGuardOutletContext>();
	const { restaurant, tables } = parentContext;
	const { isError: matterportError } = useMatterport();

	const location = useLocation();
	const checkoutRouteMatch = useMatch(`${restaurant.slug}/checkout/*`);
	const inboundRequestRouteMatch = useMatch(`${restaurant.slug}/explore/request/*`);
	const filtersRouteMatch = useMatch(`${restaurant.slug}/explore/filters`);
	const availabilityRouteMatch = location.pathname.includes('/availability');
	const { isSmallScreen } = useScreenSize();
	const { isVirtualView, turnOffVirtualView } = useViewToggle();

	// Refs and state for animated width
	const childrenRef = useRef<HTMLDivElement>(null);
	const [childrenWidth, setChildrenWidth] = useState<number>(0);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isSmallScreen || matterportError) {
			turnOffVirtualView();
		}
	}, [isSmallScreen, matterportError]);

	// Measure children content width
	useEffect(() => {
		const updateWidth = () => {
			if (childrenRef.current) {
				const width = childrenRef.current.scrollWidth;
				setChildrenWidth(width);
			}
		};

		// Small delay to ensure children are rendered
		const timer = setTimeout(updateWidth, 50);

		// Update width on window resize
		window.addEventListener('resize', updateWidth);
		return () => {
			clearTimeout(timer);
			window.removeEventListener('resize', updateWidth);
		};
	}, [children]);

	// Calculate width
	const getSidebarWidth = () => {
		if (isVirtualView || isSmallScreen) {
			return '100%';
		}
		if (isLoading || childrenWidth === 0) {
			return '400px'; // Default width while loading
		}
		return `${childrenWidth}px`;
	};

	return (
		<main className="h-full sm:flex sm:flex-col sm:gap-3 sm:p-3">
			<div className="hidden sm:block">
				<DesktopHeader />
			</div>
			<div className={`relative flex gap-3 flex-1 h-full sm:h-auto sm:flex-1 sm:min-h-0 min-h-0 ${isVirtualView ? 'overflow-hidden' : ''}`}>
				<ScrollToHashElement />
				<motion.section
					id="sidebar-container"
					className={`sm:rounded-2xl sm:border-[1.5px] sm:border-tablz-med-gray-4 sm:relative sm:overflow-auto ${isVirtualView && isSmallScreen ? 'absolute inset-0' : ''} z-10`}
					layout
					style={{
						width: getSidebarWidth(),
						minWidth: isVirtualView && isSmallScreen ? '100%' : 'fit-content',
						flexShrink: 0
					}}
					transition={{
						type: 'spring',
						stiffness: 300,
						damping: 40
					}}
					onLayoutAnimationStart={() => setIsLoading(true)}
					onLayoutAnimationComplete={() => setIsLoading(false)}
				>
					<div ref={childrenRef} className="w-screen sm:w-fit h-full sm:min-h-0 relative bg-tablz-dark-gray-1">
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: isSmallScreen ? 0 : 0.3 }}
							className="h-full"
						>
							{children}
						</motion.div>
					</div>
					<ToastContainer position="bottom-left" theme="dark" pauseOnFocusLoss={false} />
					<ModalContainer />
				</motion.section>

				<motion.section
					className={`
						${isVirtualView ? 'scrollLock' : 'hidden'}
						sm:block h-full w-full overflow-hidden rounded-none sm:h-auto sm:rounded-2xl sm:border-[1.5px] sm:border-tablz-med-gray-4 sm:relative sm:overflow-hidden m-0 sm:flex-1
						${checkoutRouteMatch || inboundRequestRouteMatch || availabilityRouteMatch ? 'select-none pointer-events-none brightness-40' : ''}
						${isVirtualView && isSmallScreen ? 'absolute inset-0' : ''}
					`}
					style={{
						zIndex: isVirtualView && isSmallScreen ? 20 : 1,
						minWidth: isVirtualView && isSmallScreen ? '100%' : '0px',
						flex: 1
					}}
					layout
					transition={{
						type: 'spring',
						stiffness: 300,
						damping: 40
					}}
				>
					{filtersRouteMatch ? (
						<AnimatedImageGallery staticImage={true} restaurant={restaurant} tables={tables} />
					) : (
						<Sentry.ErrorBoundary
							fallback={() => <AnimatedImageGallery staticImage={true} restaurant={restaurant} tables={tables} />}
							beforeCapture={(scope) => {
								scope.setTag('location', 'App Layout');
							}}
						>
							{restaurant?.layouts?.map((layout) => (
								<MatterportEnvironment
									key={layout.model}
									restaurantId={restaurant.id}
									modelId={layout.model}
									brightness={layout.brightness}
									contrast={layout.contrast}
									exposure={layout.exposure}
									highlights={layout.highlights}
									shadows={layout.shadows}
									temperature={layout.temperature}
									saturation={layout.saturation}
									condition={layout.condition}
								/>
							)) || <AnimatedImageGallery staticImage={true} restaurant={restaurant} tables={tables} />}
						</Sentry.ErrorBoundary>
					)}
				</motion.section>
			</div>
		</main>
	);
}
